import clsx from 'clsx'
import styles from './styles.module.scss'
import { IProgressProps, IProgressRange } from './types'

export default function Progress({
  max,
  min = 0,
  value,
  variant = 'linear',
  ...props
}: IProgressProps) {
  switch (variant) {
    case 'labelled':
      return <Labelled value={value} max={max} min={min} {...props} />
    case 'circular':
      return <Circular value={value} max={max} min={min} {...props} />
    case 'linear':
    default:
      return <Linear value={value} max={max} min={min} {...props} />
  }
}

const Linear = ({ max, min = 0, value, ...props }: IProgressRange) => {
  const percentage = ((value - min) / (max - min)) * 100

  return (
    <div
      aria-valuemax={max}
      aria-valuemin={min}
      aria-valuenow={value}
      className={styles.linear_wrapper}
      role="progressbar"
      aria-label={'Progress Bar'}
      {...props}
    >
      <div className={styles.fill} style={{ width: `${percentage}%` }} />
    </div>
  )
}

const Labelled = ({ max, min = 0, value, decimalPlaces = 0, ...props }: IProgressRange) => {
  const percentage = (((value < min ? min : value) - min) / (max - min)) * 100
  const formattedPercentage = percentage.toFixed(Math.max(decimalPlaces, 0))

  return (
    <div
      aria-valuemax={max}
      aria-valuemin={min}
      aria-valuenow={value}
      className={clsx('caption-14-medium', styles.labelled_wrapper)}
      role="progressbar"
      aria-label={'Progress Bar'}
      {...props}
    >
      <div className={styles.fill} style={{ width: `${formattedPercentage}%` }}>
        <span className={styles.label}>{formattedPercentage}%</span>
      </div>
    </div>
  )
}

const Circular = ({ max, min = 0, value, ...props }: IProgressRange) => {
  const percentage = ((value - min) / (max - min)) * 100

  // Make sure we fill the circle even if the percentage is slightly off
  const cappedPercentage = Math.min(Math.max(percentage, 0), 100)

  const size = 48
  const strokeWidth = 5
  const circleCenter = size / 2
  const circleRadius = circleCenter - strokeWidth / 2
  const circumference = 2 * Math.PI * circleRadius

  // How much of the circle should not be visible as progress
  const offset = ((100 - cappedPercentage) / 100) * circumference

  return (
    <div
      aria-valuemax={max}
      aria-valuemin={min}
      aria-valuenow={value}
      className={styles.circular_wrapper}
      role="progressbar"
      aria-label={'Progress Bar'}
      {...props}
    >
      <svg
        className={styles.circle}
        fill="none"
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        width={size}
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle
          cx={circleCenter}
          cy={circleCenter}
          fill="none"
          r={circleRadius}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeWidth={strokeWidth}
          transform={`rotate(-90 ${circleCenter} ${circleCenter})`}
        />
      </svg>
      <div className={clsx('caption-10-medium', styles.label_wrapper)}>
        <span className={styles.label}>{Math.round(percentage)}%</span>
      </div>
    </div>
  )
}
